/**
 * Admin · Circles of Care dashboard
 *
 * Feb 23 2026 · Michael. "How can we monitor that?" — the answer is
 * this page. Three tabs + a summary strip. Read-only reporting with
 * one write action (allocate from the pool queue).
 *
 *   Metrics strip
 *   ├── Recipients tab · every named + pool-allocated gift, with
 *   │                   status: invited → joined → listening.
 *   ├── Pool queue tab · pending gifts awaiting allocation.
 *   │                   One-tap "Allocate to name/email" form.
 *   └── Donors tab    · rollup — total lights, first + last gift.
 *
 * CSV export per tab. Admin-only (backend enforces).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem("reclaim_token") || localStorage.getItem("token");
const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

// Foundations-adjacent palette so the admin experience feels like
// part of the same house, not a raw table.
const TOK = {
  bg: "#080d18",
  panel: "#0f1626",
  panelDeep: "#0a1120",
  cream: "#f5ecd9",
  creamDim: "rgba(245,236,217,0.72)",
  creamMist: "rgba(245,236,217,0.42)",
  gold: "#e0a97a",
  goldSoft: "rgba(224,169,122,0.9)",
  goldMist: "rgba(224,169,122,0.24)",
  danger: "#e07a6a",
};
const SERIF = '"Playfair Display", "Cormorant Garamond", serif';
const SANS = '"Manrope", "Helvetica Neue", Helvetica, Arial, sans-serif';

const TABS = [
  { key: "recipients", label: "Recipients" },
  { key: "pool", label: "Pool queue" },
  { key: "donors", label: "Donors" },
];

export default function AdminCirclesOfCare() {
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("recipients");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [preferenceFilter, setPreferenceFilter] = useState("");
  const [error, setError] = useState(null);
  const [allocatingFor, setAllocatingFor] = useState(null);

  const loadSummary = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/circles/summary`, auth());
      setSummary(r.data);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        setError("Admin sign-in required.");
      }
    }
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `${API}/api/admin/circles/${tab}?limit=300`;
      if (tab === "recipients") {
        if (statusFilter) url += `&status=${statusFilter}`;
        if (q.trim()) url += `&q=${encodeURIComponent(q.trim())}`;
      } else if (tab === "pool") {
        if (preferenceFilter) url += `&preference=${preferenceFilter}`;
      }
      const r = await axios.get(url, auth());
      setRows(r.data?.rows || []);
    } catch (e) {
      setRows([]);
      setError(e?.response?.data?.detail || "Load failed.");
    } finally {
      setLoading(false);
    }
  }, [tab, statusFilter, preferenceFilter, q]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const copyInvite = (token) => {
    const link = `${window.location.origin}/circles/redeem/${token}`;
    try { navigator.clipboard.writeText(link); } catch (_) { /* older browsers */ }
  };

  const exportCsv = () => {
    if (!rows.length) return;
    const keys = Object.keys(rows[0]).filter((k) => k !== "_id");
    const escape = (v) => {
      if (v === null || v === undefined) return "";
      const s = typeof v === "object" ? JSON.stringify(v) : String(v);
      return `"${s.replace(/"/g, '""')}"`;
    };
    const csv = [keys.join(","), ...rows.map((r) => keys.map((k) => escape(r[k])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `circles_${tab}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="min-h-screen"
      style={{ background: TOK.bg, color: TOK.cream }}
      data-testid="admin-circles-page"
    >
      <div className="max-w-5xl mx-auto px-5 pt-8 pb-24">
        <div className="flex items-center justify-between">
          <button
            onClick={() => nav("/admin")}
            className="uppercase"
            style={{
              background: "transparent", color: TOK.creamMist, fontFamily: SANS,
              fontSize: "0.58rem", letterSpacing: "0.28em", padding: "8px 4px",
            }}
            data-testid="admin-circles-back"
          >
            ← Admin
          </button>
          <div
            className="uppercase"
            style={{ color: TOK.goldSoft, fontFamily: SANS, fontSize: "0.6rem", letterSpacing: "0.3em" }}
          >
            One Million Lights
          </div>
        </div>

        <h1
          className="mt-4 italic"
          style={{ fontFamily: SERIF, fontSize: "clamp(2rem, 6vw, 2.6rem)", fontWeight: 300 }}
        >
          Circles of Care
        </h1>
        <p
          className="mt-2 max-w-2xl"
          style={{ color: TOK.creamDim, fontFamily: SANS, fontSize: "0.95rem", lineHeight: 1.55 }}
        >
          Every completed donation lands here. Named gifts appear as recipients you can send an
          invite link to. Pool gifts sit in the queue until you allocate them to a carer or the
          person they care for.
        </p>

        {/* Metrics */}
        <MetricsStrip summary={summary} />

        {/* Tabs */}
        <div className="mt-8 flex items-center gap-2 border-b" style={{ borderColor: TOK.goldMist }}>
          {TABS.map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => { setTab(t.key); setQ(""); }}
                data-testid={`admin-circles-tab-${t.key}`}
                className="uppercase"
                style={{
                  background: "transparent",
                  color: active ? TOK.gold : TOK.creamMist,
                  fontFamily: SANS,
                  fontSize: "0.72rem",
                  letterSpacing: "0.24em",
                  padding: "12px 14px",
                  borderBottom: active ? `2px solid ${TOK.gold}` : "2px solid transparent",
                }}
              >
                {t.label}
              </button>
            );
          })}
          <div className="ml-auto" />
          <button
            onClick={exportCsv}
            disabled={!rows.length}
            className="uppercase"
            style={{
              background: "transparent", color: rows.length ? TOK.goldSoft : TOK.creamMist,
              fontFamily: SANS, fontSize: "0.6rem", letterSpacing: "0.28em",
              padding: "10px 12px", border: `1px solid ${TOK.goldMist}`, borderRadius: 999,
              cursor: rows.length ? "pointer" : "not-allowed",
            }}
            data-testid="admin-circles-export"
          >
            Export CSV
          </button>
        </div>

        {/* Filters */}
        {tab === "recipients" && (
          <div className="mt-4 flex gap-2 flex-wrap">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, note…"
              className="rounded-full px-4 py-2 flex-1 min-w-[220px]"
              style={{
                background: TOK.panelDeep, border: `1px solid ${TOK.goldMist}`,
                color: TOK.cream, fontFamily: SANS, fontSize: "0.88rem", outline: "none",
              }}
              data-testid="admin-circles-search"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-full px-4 py-2"
              style={{
                background: TOK.panelDeep, border: `1px solid ${TOK.goldMist}`,
                color: TOK.cream, fontFamily: SANS, fontSize: "0.85rem", outline: "none",
              }}
              data-testid="admin-circles-status-filter"
            >
              <option value="">All statuses</option>
              <option value="invited">Invited</option>
              <option value="joined">Joined</option>
              <option value="listening">Listening</option>
            </select>
          </div>
        )}
        {tab === "pool" && (
          <div className="mt-4 flex gap-2 flex-wrap">
            <select
              value={preferenceFilter}
              onChange={(e) => setPreferenceFilter(e.target.value)}
              className="rounded-full px-4 py-2"
              style={{
                background: TOK.panelDeep, border: `1px solid ${TOK.goldMist}`,
                color: TOK.cream, fontFamily: SANS, fontSize: "0.85rem", outline: "none",
              }}
              data-testid="admin-circles-pref-filter"
            >
              <option value="">All preferences</option>
              <option value="carer">Carer</option>
              <option value="caree">Caree</option>
              <option value="either">Either</option>
            </select>
          </div>
        )}

        {/* Table */}
        <div className="mt-4">
          {error && <p style={{ color: TOK.danger, fontFamily: SANS }}>{error}</p>}
          {loading ? (
            <p style={{ color: TOK.creamMist, fontFamily: SANS }}>Loading…</p>
          ) : rows.length === 0 ? (
            <p style={{ color: TOK.creamMist, fontFamily: SANS, padding: "24px 0" }}>
              Nothing here yet.
            </p>
          ) : tab === "recipients" ? (
            <RecipientsTable rows={rows} copyInvite={copyInvite} />
          ) : tab === "pool" ? (
            <PoolTable
              rows={rows}
              onAllocate={(row) => setAllocatingFor(row)}
            />
          ) : (
            <DonorsTable rows={rows} />
          )}
        </div>
      </div>

      {allocatingFor && (
        <AllocateModal
          pool={allocatingFor}
          onClose={() => setAllocatingFor(null)}
          onDone={() => {
            setAllocatingFor(null);
            loadRows();
            loadSummary();
          }}
        />
      )}
    </div>
  );
}

function MetricsStrip({ summary }) {
  const cells = [
    { label: "Recipients", value: summary?.total_recipients ?? "—" },
    { label: "Joined", value: summary?.recipients_joined ?? "—" },
    { label: "Pool lights", value: summary?.pool_lights_available ?? "—" },
    { label: "Donors", value: summary?.distinct_donors ?? "—" },
  ];
  return (
    <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
      {cells.map((c) => (
        <div
          key={c.label}
          className="rounded-2xl p-4"
          style={{
            background: TOK.panel,
            border: `1px solid ${TOK.goldMist}`,
          }}
          data-testid={`admin-circles-metric-${c.label.toLowerCase().replace(/ /g, "-")}`}
        >
          <div
            className="uppercase"
            style={{
              color: TOK.goldSoft, fontFamily: SANS,
              fontSize: "0.56rem", letterSpacing: "0.28em",
            }}
          >
            {c.label}
          </div>
          <div
            className="italic mt-1"
            style={{
              color: TOK.cream, fontFamily: SERIF,
              fontSize: "1.8rem", fontWeight: 300,
            }}
          >
            {c.value}
          </div>
        </div>
      ))}
    </div>
  );
}

function Table({ children }) {
  return (
    <div className="overflow-x-auto rounded-2xl" style={{ background: TOK.panel, border: `1px solid ${TOK.goldMist}` }}>
      <table className="w-full text-left" style={{ borderCollapse: "collapse" }}>
        {children}
      </table>
    </div>
  );
}

const th = { color: TOK.goldSoft, fontFamily: SANS, fontSize: "0.6rem", letterSpacing: "0.2em", textTransform: "uppercase", padding: "12px 14px", borderBottom: `1px solid ${TOK.goldMist}`, whiteSpace: "nowrap" };
const td = { color: TOK.cream, fontFamily: SANS, fontSize: "0.88rem", padding: "12px 14px", borderBottom: `1px solid rgba(245,236,217,0.06)`, verticalAlign: "top" };

function RecipientsTable({ rows, copyInvite }) {
  const [busyId, setBusyId] = useState(null);
  const resendInvite = async (r) => {
    setBusyId(r.id);
    try {
      await axios.post(`${API}/api/admin/circles/recipient/${r.id}/resend`, {}, auth());
      // Optimistic: mark it sent in the local row.
      r.invite_sent = true;
      r.invite_sent_at = new Date().toISOString();
    } catch (e) {
      // Non-blocking — the message shows up in the row's tooltip.
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Could not resend.");
    } finally {
      setBusyId(null);
    }
  };
  return (
    <Table>
      <thead>
        <tr>
          <th style={th}>Recipient</th>
          <th style={th}>Status</th>
          <th style={th}>Invite</th>
          <th style={th}>Donor</th>
          <th style={th}>Source</th>
          <th style={th}>Created</th>
          <th style={th}>Actions</th>
        </tr>
      </thead>
      <tbody data-testid="admin-circles-recipients-body">
        {rows.map((r) => (
          <tr key={r.id}>
            <td style={td}>
              <div style={{ fontWeight: 600 }}>{r.recipient_name || "—"}</div>
              <div style={{ color: TOK.creamDim, fontSize: "0.78rem" }}>
                {r.recipient_email || <span style={{ color: TOK.creamMist }}>(no email)</span>}
              </div>
              {r.note && (
                <div style={{ color: TOK.creamMist, fontSize: "0.78rem", fontStyle: "italic", marginTop: 4 }}>
                  "{r.note}"
                </div>
              )}
            </td>
            <td style={td}>
              <StatusPill status={r.status} />
            </td>
            <td style={td}>
              {r.invite_sent ? (
                <span
                  className="uppercase"
                  style={{
                    background: "rgba(174,225,180,0.14)", color: "#a8e0b0",
                    fontFamily: SANS, fontSize: "0.55rem", letterSpacing: "0.22em",
                    padding: "4px 10px", borderRadius: 999,
                  }}
                  title={r.invite_sent_at || ""}
                  data-testid={`admin-circles-invite-sent-${r.id}`}
                >
                  ✉ sent
                </span>
              ) : (
                <span
                  className="uppercase"
                  style={{
                    color: TOK.creamMist, fontFamily: SANS,
                    fontSize: "0.55rem", letterSpacing: "0.22em",
                  }}
                >
                  {r.recipient_email ? "not sent" : "no email"}
                </span>
              )}
            </td>
            <td style={td}>
              {r.anonymous ? (
                <span style={{ color: TOK.creamMist }}>Anonymous</span>
              ) : (
                <span>{r.donor_email || <span style={{ color: TOK.creamMist }}>(no email)</span>}</span>
              )}
            </td>
            <td style={td}>
              <span style={{ color: TOK.creamDim, fontSize: "0.78rem" }}>{r.source}</span>
            </td>
            <td style={td}>
              <span style={{ color: TOK.creamMist, fontSize: "0.78rem" }}>{fmtDate(r.created_at)}</span>
            </td>
            <td style={td}>
              <div className="flex flex-col gap-1.5">
                <button
                  onClick={() => copyInvite(r.magic_link_token)}
                  className="uppercase"
                  style={{
                    background: "transparent", color: TOK.gold, border: `1px solid ${TOK.goldMist}`,
                    padding: "6px 10px", borderRadius: 999, fontFamily: SANS, fontSize: "0.58rem", letterSpacing: "0.22em",
                    cursor: "pointer",
                  }}
                  data-testid={`admin-circles-copy-${r.id}`}
                >
                  Copy link
                </button>
                {r.recipient_email && (
                  <button
                    onClick={() => resendInvite(r)}
                    disabled={busyId === r.id}
                    className="uppercase"
                    style={{
                      background: "transparent", color: TOK.creamDim,
                      border: `1px solid ${TOK.goldMist}`,
                      padding: "6px 10px", borderRadius: 999, fontFamily: SANS,
                      fontSize: "0.58rem", letterSpacing: "0.22em",
                      cursor: busyId === r.id ? "default" : "pointer",
                      opacity: busyId === r.id ? 0.5 : 1,
                    }}
                    data-testid={`admin-circles-resend-${r.id}`}
                  >
                    {busyId === r.id ? "Sending…" : r.invite_sent ? "Resend" : "Send now"}
                  </button>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function PoolTable({ rows, onAllocate }) {
  return (
    <Table>
      <thead>
        <tr>
          <th style={th}>Available</th>
          <th style={th}>Preference</th>
          <th style={th}>Note</th>
          <th style={th}>Donor</th>
          <th style={th}>Status</th>
          <th style={th}>Created</th>
          <th style={th}></th>
        </tr>
      </thead>
      <tbody data-testid="admin-circles-pool-body">
        {rows.map((r) => (
          <tr key={r.id}>
            <td style={td}>
              <div style={{ fontWeight: 600 }}>{r.memberships_available} / {r.memberships_total}</div>
            </td>
            <td style={td}>
              <span style={{ textTransform: "capitalize" }}>{r.preference}</span>
            </td>
            <td style={td}>
              {r.note ? (
                <span style={{ color: TOK.creamDim, fontStyle: "italic" }}>"{r.note}"</span>
              ) : (
                <span style={{ color: TOK.creamMist }}>—</span>
              )}
            </td>
            <td style={td}>
              {r.anonymous ? (
                <span style={{ color: TOK.creamMist }}>Anonymous</span>
              ) : (
                <span>{r.donor_email || <span style={{ color: TOK.creamMist }}>(no email)</span>}</span>
              )}
            </td>
            <td style={td}>
              <StatusPill status={r.status} />
            </td>
            <td style={td}>
              <span style={{ color: TOK.creamMist, fontSize: "0.78rem" }}>{fmtDate(r.created_at)}</span>
            </td>
            <td style={td}>
              {r.memberships_available > 0 && (
                <button
                  onClick={() => onAllocate(r)}
                  className="uppercase"
                  style={{
                    background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
                    color: "#1a0f05", border: "1px solid #f5e0c5",
                    padding: "8px 14px", borderRadius: 999,
                    fontFamily: SANS, fontSize: "0.6rem", letterSpacing: "0.22em",
                    cursor: "pointer",
                  }}
                  data-testid={`admin-circles-allocate-${r.id}`}
                >
                  Allocate
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function DonorsTable({ rows }) {
  return (
    <Table>
      <thead>
        <tr>
          <th style={th}>Donor</th>
          <th style={th}>Lights</th>
          <th style={th}>Gifts</th>
          <th style={th}>Sources</th>
          <th style={th}>First gift</th>
          <th style={th}>Last gift</th>
        </tr>
      </thead>
      <tbody data-testid="admin-circles-donors-body">
        {rows.map((d) => (
          <tr key={d.id}>
            <td style={td}>
              <div style={{ fontWeight: 600 }}>{d.donor_name || d.donor_email || "Anonymous"}</div>
              {d.donor_email && d.donor_name && (
                <div style={{ color: TOK.creamDim, fontSize: "0.78rem" }}>{d.donor_email}</div>
              )}
              {d.anonymous_count > 0 && (
                <div style={{ color: TOK.creamMist, fontSize: "0.72rem", fontStyle: "italic", marginTop: 2 }}>
                  {d.anonymous_count} anonymous
                </div>
              )}
            </td>
            <td style={td}>{d.lights_total}</td>
            <td style={td}>{d.gifts_count}</td>
            <td style={td}>{(d.sources || []).join(", ")}</td>
            <td style={td}><span style={{ color: TOK.creamMist, fontSize: "0.78rem" }}>{fmtDate(d.first_gift_at)}</span></td>
            <td style={td}><span style={{ color: TOK.creamMist, fontSize: "0.78rem" }}>{fmtDate(d.last_gift_at)}</span></td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function StatusPill({ status }) {
  const map = {
    invited: { bg: "rgba(245,236,217,0.06)", fg: TOK.creamDim, label: "Invited" },
    joined: { bg: "rgba(224,169,122,0.14)", fg: TOK.gold, label: "Joined" },
    listening: { bg: "rgba(174,225,180,0.14)", fg: "#a8e0b0", label: "Listening" },
    pending: { bg: "rgba(245,236,217,0.06)", fg: TOK.creamDim, label: "Pending" },
    partial: { bg: "rgba(224,169,122,0.14)", fg: TOK.gold, label: "Partial" },
    fulfilled: { bg: "rgba(174,225,180,0.14)", fg: "#a8e0b0", label: "Fulfilled" },
  };
  const s = map[status] || { bg: "rgba(245,236,217,0.06)", fg: TOK.creamDim, label: status };
  return (
    <span
      className="uppercase"
      style={{
        background: s.bg, color: s.fg,
        fontFamily: SANS, fontSize: "0.56rem", letterSpacing: "0.22em",
        padding: "4px 10px", borderRadius: 999,
      }}
    >
      {s.label}
    </span>
  );
}

function AllocateModal({ pool, onClose, onDone }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [count, setCount] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const available = pool.memberships_available;
  const submit = async () => {
    if (!name.trim()) { setError("Name required."); return; }
    if (count < 1 || count > available) { setError(`Choose 1..${available} lights.`); return; }
    setBusy(true);
    setError(null);
    try {
      await axios.post(
        `${API}/api/admin/circles/pool/${pool.id}/allocate`,
        { recipient_name: name.trim(), recipient_email: email.trim() || undefined, note: note.trim() || undefined, count },
        auth(),
      );
      onDone();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not allocate.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      style={{ background: "rgba(6,14,31,0.94)", backdropFilter: "blur(10px)" }}
      onClick={onClose}
      data-testid="admin-circles-allocate-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-w-md mx-auto rounded-2xl p-6 mt-24"
        style={{ background: TOK.panel, border: `1px solid ${TOK.goldMist}`, color: TOK.cream }}
      >
        <div
          className="uppercase"
          style={{ color: TOK.goldSoft, fontFamily: SANS, fontSize: "0.58rem", letterSpacing: "0.28em" }}
        >
          Allocate — {available} light{available === 1 ? "" : "s"} available
        </div>
        <h3 className="italic mt-2" style={{ fontFamily: SERIF, fontSize: "1.55rem", fontWeight: 300 }}>
          Send this to…
        </h3>
        <div className="mt-4 space-y-3">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Recipient name"
            className="w-full rounded-lg px-3 py-2"
            style={{ background: TOK.panelDeep, border: `1px solid ${TOK.goldMist}`, color: TOK.cream, fontFamily: SANS, fontSize: "0.95rem", outline: "none" }}
            data-testid="admin-circles-allocate-name"
          />
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email (optional)"
            className="w-full rounded-lg px-3 py-2"
            style={{ background: TOK.panelDeep, border: `1px solid ${TOK.goldMist}`, color: TOK.cream, fontFamily: SANS, fontSize: "0.9rem", outline: "none" }}
            data-testid="admin-circles-allocate-email"
          />
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Short note (optional)"
            className="w-full rounded-lg px-3 py-2"
            style={{ background: TOK.panelDeep, border: `1px solid ${TOK.goldMist}`, color: TOK.cream, fontFamily: SANS, fontSize: "0.88rem", outline: "none" }}
            data-testid="admin-circles-allocate-note"
          />
          <input
            type="number" min={1} max={available}
            value={count}
            onChange={(e) => setCount(Math.max(1, Math.min(available, Number(e.target.value) || 1)))}
            className="w-full rounded-lg px-3 py-2"
            style={{ background: TOK.panelDeep, border: `1px solid ${TOK.goldMist}`, color: TOK.cream, fontFamily: SANS, fontSize: "0.88rem", outline: "none" }}
            data-testid="admin-circles-allocate-count"
          />
        </div>
        {error && <p style={{ color: TOK.danger, fontFamily: SANS, marginTop: 8 }}>{error}</p>}
        <div className="mt-6 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 uppercase rounded-full"
            style={{
              background: "transparent", border: `1px solid ${TOK.goldMist}`,
              color: TOK.creamDim, fontFamily: SANS, fontSize: "0.7rem", letterSpacing: "0.24em", padding: "12px",
            }}
            data-testid="admin-circles-allocate-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className="flex-1 uppercase rounded-full"
            style={{
              background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
              color: "#1a0f05", border: "1px solid #f5e0c5",
              fontFamily: SANS, fontSize: "0.72rem", letterSpacing: "0.22em", padding: "12px",
              opacity: busy ? 0.6 : 1,
            }}
            data-testid="admin-circles-allocate-submit"
          >
            {busy ? "Allocating…" : "Allocate"}
          </button>
        </div>
      </div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch (_) {
    return String(iso).slice(0, 10);
  }
}
